import { useState, useEffect } from 'react';
import { MapPin, Mail, Globe, ArrowLeft, Share2, Download, Linkedin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const audits = [
  { title: 'Stratégies Corporate', items: ['Spécialisation & Diversification', 'Croissance interne', 'Fusion / Acquisition', 'Alliance'] },
  { title: 'Stratégies Business', items: ['Concurrentielles'] },
  { title: "Stratégies d'Innovation", items: ['Transformation & Disruption'] },
  { title: 'Stratégie à l\'International', items: ['Internationalisation'] },
  { title: 'Stratégie 360°', items: ['Diagnostic complet'] },
];

const strengths = [
  { label: 'Expertise globale', desc: 'Audit, Conseil & Management de transition' },
  { label: 'Approche pragmatique', desc: 'Solutions sur-mesure' },
];

export const BusinessCard = () => {
  const [qrImageUrl, setQrImageUrl] = useState('');

  useEffect(() => {
    fetch(`${API}/qr-code?url=${encodeURIComponent('https://www.bicenter-consulting.com/carte-visite')}&size=600`)
      .then(res => res.blob())
      .then(blob => setQrImageUrl(URL.createObjectURL(blob)))
      .catch(() => {});
  }, []);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Bicenter Consulting - Laurianne NGOUNOU',
          text: 'Cabinet de conseil en stratégie & performance. Audits, Conseils et Management de transition.',
          url: 'https://www.bicenter-consulting.com'
        });
      } catch (e) { /* cancelled */ }
    } else {
      navigator.clipboard.writeText('https://www.bicenter-consulting.com');
      alert('Lien copié !');
    }
  };

  return (
    <div className="min-h-screen bg-[#002e27] flex flex-col items-center" data-testid="business-card-page">
      {/* Top bar */}
      <div className="w-full max-w-md px-4 pt-4 flex justify-between items-center">
        <a href="/" className="text-white/60 hover:text-white text-sm flex items-center gap-1" data-testid="back-home">
          <ArrowLeft className="w-4 h-4" /> Retour
        </a>
        <button
          onClick={handleShare}
          className="text-white/60 hover:text-white text-sm flex items-center gap-1"
          data-testid="share-button"
        >
          <Share2 className="w-4 h-4" /> Partager
        </button>
      </div>

      {/* Card */}
      <div className="w-full max-w-md mx-auto px-4 py-6">
        {/* Header */}
        <div className="bg-gradient-to-br from-[#004d40] to-[#00332b] rounded-t-3xl px-6 pt-8 pb-6 text-center relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-[#4caf50]/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#4caf50]/8 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative z-10">
            <h1 className="font-serif text-3xl font-bold text-white tracking-tight">
              BICENTER
            </h1>
            <span className="text-[#4caf50] font-bold text-xl tracking-wide">
              CONSULTING
            </span>
            <p className="text-white/50 text-xs mt-1 italic tracking-widest uppercase">your business partner</p>
            
            <div className="mt-6 w-16 h-[2px] bg-[#4caf50] mx-auto" />
            
            <h2 className="mt-5 text-white text-xl font-semibold" data-testid="founder-name">
              Laurianne NGOUNOU
            </h2>
            <p className="text-[#4caf50] text-sm font-medium mt-1" data-testid="founder-title">
              Fondatrice
            </p>
            <p className="text-white/70 text-xs mt-1">
              Audits & Conseils en Stratégie d'Entreprise
            </p>
          </div>
        </div>

        {/* QR Code section */}
        <div className="bg-white px-6 py-6 flex flex-col items-center">
          <p className="text-[#004d40] text-xs font-semibold uppercase tracking-widest mb-3">
            Scannez pour accéder à ma carte de visite
          </p>
          <div className="bg-[#f0f7f5] rounded-2xl p-3 shadow-inner" data-testid="qr-code-container">
            {qrImageUrl ? (
              <img
                src={qrImageUrl}
                alt="QR Code Bicenter Consulting"
                className="w-44 h-44 object-contain"
                data-testid="qr-code-image"
              />
            ) : (
              <div className="w-44 h-44 flex items-center justify-center text-[#004d40]/40 text-sm">
                Chargement...
              </div>
            )}
          </div>
          <p className="text-[#004d40]/60 text-[10px] mt-2">www.bicenter-consulting.com</p>
        </div>

        {/* Audits & Expertises */}
        <div className="bg-[#f8faf9] px-6 py-6">
          <h3 className="text-[#004d40] text-sm font-bold uppercase tracking-widest mb-4 text-center" data-testid="audits-title">
            Nos Audits & Expertises
          </h3>
          
          <div className="space-y-3">
            {audits.map((audit, i) => (
              <div key={i} className="bg-white rounded-xl px-4 py-3 shadow-sm border border-[#e8f0ed]">
                <p className="text-[#004d40] font-semibold text-sm">
                  Audit {audit.title}
                </p>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {audit.items.map((item, j) => (
                    <span
                      key={j}
                      className="text-[10px] bg-[#e8f5e9] text-[#2e7d32] px-2 py-0.5 rounded-full font-medium"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Strengths */}
          <div className="mt-4 grid grid-cols-2 gap-2">
            {strengths.map((s, i) => (
              <div key={i} className="bg-[#004d40] rounded-xl px-3 py-3 text-center">
                <p className="text-[#4caf50] text-xs font-bold">{s.label}</p>
                <p className="text-white/80 text-[10px] mt-0.5">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-[#004d40] rounded-b-3xl px-6 py-6" data-testid="contact-section">
          <h3 className="text-white/50 text-[10px] font-bold uppercase tracking-widest mb-4 text-center">
            Coordonnées
          </h3>
          
          <div className="space-y-3">
            <a href="https://www.linkedin.com/in/laurianne-ngounou" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white/90 hover:text-[#0a66c2] transition-colors group">
              <div className="w-8 h-8 rounded-full bg-[#0a66c2]/20 flex items-center justify-center group-hover:bg-[#0a66c2]/30 transition-colors">
                <Linkedin className="w-4 h-4 text-[#0a66c2]" />
              </div>
              <div>
                <span className="text-sm block">Laurianne NGOUNOU</span>
                <span className="text-[10px] text-white/50">Consultante experte en stratégie et performance</span>
              </div>
            </a>

            <a href="mailto:cabinet@bicenter-consulting.com" className="flex items-center gap-3 text-white/90 hover:text-[#4caf50] transition-colors group">
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-[#4caf50]/20 transition-colors">
                <Mail className="w-4 h-4" />
              </div>
              <span className="text-sm">cabinet@bicenter-consulting.com</span>
            </a>
            
            <a href="https://www.bicenter-consulting.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white/90 hover:text-[#4caf50] transition-colors group">
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-[#4caf50]/20 transition-colors">
                <Globe className="w-4 h-4" />
              </div>
              <span className="text-sm">www.bicenter-consulting.com</span>
            </a>
            
            <div className="flex items-center gap-3 text-white/90">
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                <MapPin className="w-4 h-4" />
              </div>
              <span className="text-sm">60 rue François 1er, 75008 Paris</span>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-white/10 text-center">
            <p className="text-white/30 text-[10px]">
              Cabinet de conseil en stratégie & performance d'entreprise
            </p>
            <p className="text-white/20 text-[10px] mt-0.5">
              France · Europe · Afrique
            </p>
          </div>
        </div>
      </div>

      {/* Bottom action */}
      <div className="w-full max-w-md px-4 pb-8">
        <a
          href={qrImageUrl || '#'}
          download="QR-Code-Carte-de-Visite-Virtuelle-Bicenter-Consulting.png"
          className="flex items-center justify-center gap-2 w-full bg-[#4caf50] hover:bg-[#388e3c] text-white py-3 rounded-full font-medium text-sm transition-colors"
          data-testid="download-qr-button"
        >
          <Download className="w-4 h-4" />
          Télécharger le QR Code Carte de Visite
        </a>
      </div>
    </div>
  );
};
